import { useCallback, useEffect, useState } from 'react';
// @mui
import { Box, Table, Switch, TableBody, TableContainer, TablePagination, FormControlLabel } from '@mui/material';
import _ from 'lodash';
// routes
import { useSelector } from 'react-redux';
import useTable, { getComparator, emptyRows } from '../../../../../hooks/useTable';
import Scrollbar from '../../../../../components/Scrollbar';
import { TableEmptyRows, TableHeadCustom, TableNoData, TableSkeleton } from '../../../../../components/table';
// sections
import RetailKYCTableRow from './IdentomatKYCTableRow';
import { adminApi } from '../../../../../utils/axios';
import { USER_KYC, USER_KYC_HIGHLIGHTS } from '../../../../../utils/apiUrl';
import IdentomatKycFilter from './IdentomatKycFilter';
import RetailKycHeaders from '../retailKycHeaders';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'id', label: 'ID', align: 'left' },
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left', minWidth: '180px' },
  { id: 'created_date', label: 'Created Date', align: 'left', minWidth: '160px' },
  { id: 'modified_date', label: 'Modified Date', align: 'left', minWidth: '160px' },
  { id: 'application_id', label: 'Application ID', align: 'left', minWidth: '180px' },
  { id: 'docType', label: 'Document Type', align: 'center', minWidth: '180px' },
  { id: 'front', label: 'Front Part of Doc', align: 'center', minWidth: '180px' },
  { id: 'back', label: 'Back Part of Doc', align: 'center', minWidth: '180px' },
  { id: 'selfie', label: 'Selfie', align: 'center', minWidth: '180px' },
  { id: 'status', label: 'Status', align: 'left', minWidth: '50px' },
];

// ----------------------------------------------------------------------

export default function RetailKYCTable() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    selected,
    onSelectRow,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const [tableData, setTableData] = useState({ count: 0, results: [] });

  const [totalData, setTotalData] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const { search, status, document_type, from_date, to_date, created_date_after, created_date_before } = useSelector(
    (state) => state.filters.identomatListKycFilters
  );

  const getRetailKyc = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(USER_KYC, {
        params: {
          offset: rowsPerPage * page,
          limit: rowsPerPage,
          search,
          status,
          document_type,
          from_date,
          to_date,
          created_date_after,
          created_date_before,
        },
      });
      setLoading(false);
      const userKycResults = _.map(response.data?.results, (user) => ({
        ...user,
        name: `${user?.user?.first_name} ${user?.user?.last_name}`,
      }));
      setTableData({
        ...response.data,
        results: userKycResults,
      });
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }, [search, status, document_type, from_date, to_date, created_date_after, created_date_before, rowsPerPage, page]);

  useEffect(() => {
    getRetailKyc();
  }, [
    search,
    status,
    document_type,
    from_date,
    to_date,
    created_date_after,
    created_date_before,
    rowsPerPage,
    page,
    getRetailKyc,
  ]);

  useEffect(() => {
    getKycHighlightsData();
  }, []);

  const getKycHighlightsData = async () => {
    try {
      const response = await adminApi.get(USER_KYC_HIGHLIGHTS);
      setTotalData(response?.data?.data);
    } catch (error) {
      console.log('error', error);
    }
  };

  const dataFiltered = applySortFilter({
    tableData: tableData.results,
    comparator: getComparator(order, orderBy),
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = tableData.results.length === 0;

  return (
    <>
      <IdentomatKycFilter />
      {totalData && <RetailKycHeaders totalData={totalData} />}
      <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
        <Scrollbar>
          <Table size={dense ? 'small' : 'medium'}>
            <TableHeadCustom
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={tableData.length}
              numSelected={selected.length}
              onSort={onSort}
            />
            {isLoading ? (
              <>
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
                <TableSkeleton />
              </>
            ) : (
              <TableBody>
                {dataFiltered.map((row) => (
                  <RetailKYCTableRow
                    key={row.id}
                    row={row}
                    selected={selected.includes(row.id)}
                    onSelectRow={() => onSelectRow(row.id)}
                  />
                ))}
                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(page, rowsPerPage, tableData.results.length)}
                />
                <TableNoData isNotFound={isNotFound} />
              </TableBody>
            )}
          </Table>
        </Scrollbar>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={tableData.count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, comparator }) {
  const stabilizedThis = tableData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  return tableData;
}
